import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { track } from './analytics';

/**
 * Tutor events (Sol mixpanel-coverage D1 A): a session starting and ending, each question the learner puts to the
 * tutor and each answer it finishes, in voice or chat. Ids, counts and seconds only - never what was said or written.
 * A refusal looks like any other answer to the player, so refusals are left to the tutor service's own events.
 */
const MODES = { VOICE: 'voice', HYBRID: 'chat', TEXT: 'text' };
export const tutorModeName = mode => MODES[String(mode || '').toUpperCase()] || 'voice';

const newId = () => {
  try {
    return `t_${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}`;
  } catch (_) {
    return `t_${Math.random().toString(16).slice(2, 18)}`;
  }
};

export function createTutorSessionTracker(send = track, now = () => Date.now()) {
  let session = null;
  const base = () => ({ tutor_session_id: session.id, mode: session.mode, lesson_id: session.lessonId });
  return {
    get open() {
      return Boolean(session);
    },
    start({ mode, lessonId }) {
      if (session) return;
      session = { id: newId(), mode: tutorModeName(mode), lessonId: lessonId || null, startedAt: now(), questions: 0, firstLeft: null, lastLeft: null };
      send('tutor_session_started', base());
    },
    question() {
      if (!session) return;
      session.questions += 1;
      send('tutor_question_submitted', { ...base(), question_count: session.questions });
    },
    answer() {
      if (!session) return;
      send('tutor_response_completed', { ...base(), question_count: session.questions });
    },
    meter(secondsLeft) {
      if (!session || !Number.isFinite(secondsLeft)) return;
      if (session.firstLeft == null) session.firstLeft = secondsLeft;
      session.lastLeft = secondsLeft;
    },
    end(reason) {
      if (!session) return;
      const used = session.firstLeft != null && session.lastLeft != null ? Math.max(0, session.firstLeft - session.lastLeft) : null;
      send('tutor_session_ended', {
        ...base(),
        question_count: session.questions,
        duration_seconds: Math.max(0, Math.round((now() - session.startedAt) / 1000)),
        seconds_used: used,
        end_reason: String(reason || 'closed').toLowerCase(),
      });
      session = null;
    },
  };
}

/** Wires the tracker to the tutor screen's own state: connection, the turn machine and the server's minutes meter. */
export function useTutorAnalytics({ active, connected, mode, turn, secondsLeft, endReasonRef }) {
  const tracker = useRef(null);
  if (!tracker.current) tracker.current = createTutorSessionTracker();
  const lessonId = useSelector(state => state.course?.currentLessonProgress?.currentLessonId || state.course?.courseData?.currentLessonId || null);
  const previousTurn = useRef(turn);
  const awaiting = useRef(false);

  useEffect(() => {
    if (active && connected) tracker.current.start({ mode, lessonId });
  }, [active, connected, mode, lessonId]);

  useEffect(() => {
    if (!active && tracker.current.open) {
      tracker.current.end(endReasonRef?.current || 'closed');
      if (endReasonRef) endReasonRef.current = null;
    }
  }, [active, endReasonRef]);

  useEffect(() => {
    const previous = previousTurn.current;
    previousTurn.current = turn;
    if (turn === 'thinking' && previous !== 'thinking') {
      awaiting.current = true;
      tracker.current.question();
    }
    if (turn === 'connecting') awaiting.current = false;
    if (turn === 'prompt' && awaiting.current && (previous === 'speaking' || previous === 'thinking')) {
      awaiting.current = false;
      tracker.current.answer();
    }
  }, [turn]);

  useEffect(() => {
    tracker.current.meter(secondsLeft);
  }, [secondsLeft]);

  // the tutor screen closing ends an open session
  useEffect(
    () => () => {
      if (tracker.current.open) tracker.current.end(endReasonRef?.current || 'closed');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
